'use client';
import React, {useContext} from "react";
import Router from "next/router";
import { useTranslator } from "../../i18n";

import Home from "./Home";
import Page404 from "./404";
import FormPage from "./Form";
import PaiementPage from "./Paiement";
import Don from "./Don";
import CustomPage from "./CustomPage";
import LinkMetaRender from "./LinkMetaRender";
import Event from "./Event";
import Events from "./Events";
import BethHabad from "./BethHabad";
import Faq from "./Faq";
import TourismInfo from "./TourismInfo";
import Horaires from "./Horaires";
import JewishZmanim from "./JewishZmanim";
import KosherPlaces from "./KosherPlaces";
import KosherPlaceDetail from "./KosherPlaces/Detail";
import Hotels from "./Hotels";
import HotelDetail from "./Hotels/Detail";
import Attractions from "./Attractions";
import AttractionDetail from "./Attractions/Detail";
import CustomSectionList from "./CustomSection/List";
import CustomSectionDetail from "./CustomSection/Detail";
import Galeries from "./Galeries";
import GalerieImages from "./GalerieImages";
import Chabbat from "./Chabbat";
import ContentHubPage from "./ContentHub";
import AccountSpacePage from "./AccountSpacePage";
import EncheresPage from "./EncheresPage";
import TravelChatbot from "./components/TravelChatbot";
import WhatsappFab from "../../components/WhatsappFab";
import logger from "../../services/logger"


export const TravelThemeContext=React.createContext({});

/**
 * Injecte les couleurs primaire/secondaire de l'organisation
 * en tant que CSS custom properties sur .travel-page
 */
function TravelThemeVars({ travelConfigTheme }) {
    if (!travelConfigTheme) return null;
    const primary = travelConfigTheme.primaryColor;
    const secondary = travelConfigTheme.secondaryColor;
    if (!primary && !secondary) return null;

    // Override NProgress avec la primary du thème.
    // NProgress est hors .travel-page (inséré dans body), donc on cible
    // directement #nprogress avec !important.
    const nprogressCss = primary ? `
#nprogress .bar { background: ${primary} !important; }
#nprogress .peg { box-shadow: 0 0 12px ${primary}, 0 0 6px ${primary} !important; }
` : "";

    const css = `.travel-page {
${primary ? `  --primary: ${primary};` : ""}
${secondary ? `  --secondary: ${secondary};` : ""}
}
${nprogressCss}`;

    return <style dangerouslySetInnerHTML={{ __html: css }} />;
}

export default function ThemeTravel({ context }) {
    const [loading, setLoading] = React.useState(false);
    const [isMobile, setIsMobile] = React.useState(context.isMobile);
    const [isIframe, setIsIframe] = React.useState(false);

    React.useEffect(() => {
        const _isIframe = window.self !== window.top;
        setIsIframe(_isIframe);

        initMobileObserver();

        const start = () => setLoading(true);
        const end = () => setLoading(false);

        Router.events.on("routeChangeStart", start);
        Router.events.on("routeChangeComplete", end);
        Router.events.on("routeChangeError", end);
        return () => {
            Router.events.off("routeChangeStart", start);
            Router.events.off("routeChangeComplete", end);
            Router.events.off("routeChangeError", end);
        };
    }, []);

    const initMobileObserver = () => {
        new ResizeObserver(function () {
            const _width = document.body.offsetWidth;
            setIsMobile(_width <= 768);
        }).observe(document.body);
    };

    const page = context.page;
    const T = useTranslator(context);

    // Raccourci vers la config Travel de l'org (couleurs, options thème)
    const travel = context.org?.travel || {};

    const _context = { ...context, isMobile, isIframe, T, travel };

    logger.debug({ org: context.org }, "ORG")
    logger.debug({ travel }, "TRAVEL")

    let Component
    switch (page.type) {
        case "HOME":
            Component=Home
            break;

        case "FORM":
            Component=FormPage
            break;

        case "PAIEMENT":
            Component=PaiementPage
            break;

        case "DON":
            Component=Don
            break;

        case "CUSTOM_PAGE":
            Component=CustomPage
            break;

        case "LINK":
            Component=LinkMetaRender
            break;

        case "EVENT":
            Component=Event
            break;

        case "EVENTS":
            Component=Events
            break;

        case "GALERIES":
            Component=Galeries
            break

        case "GALERIES_IMAGES":
            Component=GalerieImages
            break

        case "CONTENT_HUB":
            Component=ContentHubPage
            break;

        case "ACCOUNT":
            Component=AccountSpacePage
            break;

        case "ENCHERES":
        case "ENCHERES_VENTE":
            Component=EncheresPage
            break;

        case "404":
            Component=Page404
            break;

        //TRAVEL

        case "TRAVEL.BETH_HABAD":
            Component=BethHabad
            break;

        case "TRAVEL.FAQ":
            Component=Faq
            break;

        case "TRAVEL.TOURISM_INFO":
            Component=TourismInfo
            break;

        case "TRAVEL.HORAIRES":
            Component=Horaires
            break;

        case "TRAVEL.JEWISH_ZMANIM":
            Component=JewishZmanim
            break;

        case "TRAVEL.KOSHER_PLACES":
            Component=KosherPlaces
            break;

        case "TRAVEL.KOSHER_PLACES_DETAIL":
            Component=KosherPlaceDetail
            break;

        case "TRAVEL.HOTELS":
            Component=Hotels
            break;

        case "TRAVEL.HOTELS_DETAIL":
            Component=HotelDetail
            break;

        case "TRAVEL.ATTRACTIONS":
            Component=Attractions
            break;

        case "TRAVEL.ATTRACTIONS_DETAIL":
            Component=AttractionDetail
            break;

        case "TRAVEL.CUSTOM_SECTION":
            // /travel/c/<slug> = liste ; /travel/c/<slug>/<itemId> = fiche.
            Component = context.route?.[3] ? CustomSectionDetail : CustomSectionList
            break;

        case "TRAVEL.CHABBAT":
            Component=Chabbat
            break;

        case "TRAVEL.FETES":
            // Fêtes préfabriquées : même composant que la liste d'événements,
            // en variante filtrée (Events lit page.type pour ne garder que les
            // événements porteurs de prefabKey).
            Component=Events
            break;

        default:
            Component=Page404
            break;
    }

    return (
        <>
            <TravelThemeContext.Provider value={_context}>
                <TravelThemeVars travelConfigTheme={travel?.theme || {}} />
                <Component context={_context} loading={loading}/>
                <TravelChatbot context={_context}/>
                <WhatsappFab context={_context} aboveChatbot/>
            </TravelThemeContext.Provider>
        </>
    )
}
